import { Box, Grid, SelectChangeEvent } from '@mui/material';
import { format, subMonths } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../hooks/useStore';
import { DatePicker, Select } from '../../../lib/components';
import Util from '../../../misc/util';
import { ExportFormatEnum } from '../store/exportFlowStore';
import { receiptBenefits } from '@pxtra-client-lib-legacy';
import {
  BookedBenefitsEnum,
  CreatePayrollExportRequest,
  CreateReceiptExportRequest,
  PayoutTypeEnum,
  ReceiptBenefitEnum
} from '@pxtra-client-lib-legacy/gen';
import { useCompanySettings, usePaymentData } from '../../../queries/company';
import { useListCustomBenefits } from '../../../queries/custom';
import { useListBonuses } from '../../../queries/bonus';
import { useExportPreferences } from '../../../queries/impex/queries';
import { useParams, useNavigate } from 'react-router';
import {
  useBenefitFilter,
  useBenefitOptions
} from '../../receipts/detailView/shared/useBenefitOptions';
import { CheckmarkCircleFilledIcon, CircleIcon } from '../../../lib/icons';
import { cn } from '../../../lib/util';
import { FeedbackAlert } from '../../../lib/components/atomic/feedback';

const LAST_DAY_WITH_LATE_POSITIONS = 10;
const BENEFITS_WITH_LATE_POSITIONS: BookedBenefitsEnum[] = [...receiptBenefits, 'sachbezug'];

const hasLateBenefitPositions = (
  today: Date,
  bookedBenefits: BookedBenefitsEnum[] | null | undefined
) =>
  today.getDate() <= LAST_DAY_WITH_LATE_POSITIONS &&
  BENEFITS_WITH_LATE_POSITIONS.some((benefit) => bookedBenefits?.includes(benefit) ?? false);

export const ExportOptions = observer(() => {
  const { companyStore, exportFlowStore } = useStore();
  const { type } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: paymentData } = usePaymentData(companyStore.company?.id ?? '');
  const { isReceiptEnabled, isCompanyEnabled } = useBenefitFilter();
  const benefitOptions = useBenefitOptions({
    deps: [type, paymentData?.booked_benefits],
    filter: (b) => {
      if (type === 'receipts' && !isReceiptEnabled(b)) return false;
      return isCompanyEnabled(b);
    }
  });

  const { data: companySettings } = useCompanySettings(
    companyStore.company?.id ?? 'MISSING_COMPANY_ID'
  );

  const { data: customBenefits, isLoading: isCustomBenefitsLoading } = useListCustomBenefits(
    companyStore.company?.id ?? '',
    paymentData?.pricing_model
  );
  const approvedCustomBenefits = useMemo(
    () => (customBenefits?.results ?? []).filter((cb) => cb.status === 'active' && cb.payroll_id),
    [customBenefits]
  );
  const approvedCustomBenefitIds = useMemo(
    () => new Set(approvedCustomBenefits.map((cb) => cb.id)),
    [approvedCustomBenefits]
  );
  const { data: bonuses } = useListBonuses(companyStore.company?.id ?? '');
  const hasBonuses = (bonuses?.results?.length ?? 0) > 0;

  const {
    data: exportPreferences,
    isFetching: isPreferencesFetching,
    isSuccess: isPreferencesSuccess
  } = useExportPreferences(companyStore.company?.id, type);

  const isDatevConfigured = useMemo(() => {
    return (
      companySettings?.datev_client_number &&
      companySettings?.datev_consultant_number &&
      type === 'payroll_data'
    );
  }, [companySettings, type]);

  const [error, setError] = useState(false);

  const getInitialFormat = (): ExportFormatEnum => {
    if (isDatevConfigured) {
      return 'datev';
    } else if (companyStore.settings?.payroll_system === 'AGENDA' && type === 'payroll_data') {
      return 'agenda';
    } else if (companyStore.settings?.payroll_system === 'SAGE' && type === 'payroll_data') {
      return 'sage';
    } else {
      return 'xlsx';
    }
  };
  const [selectedFormat, setSelectedFormat] = useState<ExportFormatEnum>(() => {
    if (exportFlowStore.data.format) {
      return exportFlowStore.data.format as ExportFormatEnum;
    }
    return getInitialFormat();
  });

  const filteredOptions = exportFlowStore.getAllowedFormats();
  useEffect(() => {
    if (exportFlowStore.data) {
      exportFlowStore.updateData({
        accounting_period_from:
          (exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest)
            ?.accounting_period_from ?? Util.getAccountingPeriod(),
        accounting_period_to:
          (exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest)
            ?.accounting_period_to ?? Util.getAccountingPeriod()
      });
    }
  }, []);

  useEffect(() => {
    if (!exportFlowStore.hasSelectedEmployees()) {
      navigate(`/exports/${type}/1`);
    }
  }, [exportFlowStore, type, navigate]);

  useEffect(() => {
    if (exportFlowStore.data.format) {
      return;
    }

    if (isPreferencesFetching || !isPreferencesSuccess || isCustomBenefitsLoading) {
      return;
    }

    if (exportPreferences) {
      if (
        exportPreferences.format &&
        (filteredOptions as string[]).includes(exportPreferences.format)
      ) {
        setSelectedFormat(exportPreferences.format as ExportFormatEnum);
        exportFlowStore.updateData({ format: exportPreferences.format });
      } else {
        exportFlowStore.updateData({ format: selectedFormat });
      }

      const filter = exportPreferences.filter;
      if (filter) {
        const validCustomBenefits = filter.custom_benefits?.filter((id) =>
          approvedCustomBenefitIds.has(id)
        );

        const hasAnyFilterValue =
          (filter.benefits?.length ?? 0) > 0 ||
          (validCustomBenefits?.length ?? 0) > 0 ||
          (filter.bonus_tax_treatments?.length ?? 0) > 0 ||
          (filter.bonus_payout_types?.length ?? 0) > 0;

        if (hasAnyFilterValue) {
          exportFlowStore.updateData({
            filter: {
              benefits: filter.benefits,
              custom_benefits: validCustomBenefits,
              bonus_tax_treatments: filter.bonus_tax_treatments,
              bonus_payout_types: filter.bonus_payout_types
            }
          });
        }
      }
    } else {
      exportFlowStore.updateData({ format: selectedFormat });
    }
  }, [
    isPreferencesFetching,
    isPreferencesSuccess,
    isCustomBenefitsLoading,
    exportPreferences,
    filteredOptions,
    selectedFormat,
    approvedCustomBenefitIds
  ]);

  const handleFormatChange = (e: SelectChangeEvent) => {
    setSelectedFormat(e.target.value as ExportFormatEnum);
    exportFlowStore.updateData({ format: e.target.value as ExportFormatEnum });
  };

  const selectedBenefits =
    (exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest)?.filter
      ?.benefits ?? [];

  const selectedCustomBenefits =
    (exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest)?.filter
      ?.custom_benefits ?? [];

  const handleCustomBenefitChange = (event: SelectChangeEvent<string[]>) => {
    const customBenefitIds =
      typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    const currentFilter = (
      exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest
    )?.filter;
    exportFlowStore.updateData({
      filter:
        customBenefitIds.length === 0 && !currentFilter?.benefits?.length
          ? null
          : { ...currentFilter, custom_benefits: customBenefitIds }
    });
  };

  const selectedTaxTreatments =
    (exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest)?.filter
      ?.bonus_tax_treatments ?? [];

  const selectedPayoutTypes =
    (exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest)?.filter
      ?.bonus_payout_types ?? [];

  const handleTaxTreatmentChange = (event: SelectChangeEvent<string[]>) => {
    const taxTreatments =
      typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    const currentFilter = (
      exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest
    )?.filter;

    const updatedAvailablePayoutTypes =
      taxTreatments.length === 0
        ? []
        : [
            ...new Set(
              (bonuses?.results ?? [])
                .filter((b) => taxTreatments.includes(b.tax_treatment ?? ''))
                .map((b) => b.payout_type)
                .filter(Boolean)
            )
          ];

    const currentPayoutTypes = currentFilter?.bonus_payout_types ?? [];
    const validPayoutTypes = currentPayoutTypes.filter((pt) =>
      updatedAvailablePayoutTypes.includes(pt)
    );

    exportFlowStore.updateData({
      filter: {
        ...currentFilter,
        bonus_tax_treatments: taxTreatments.length === 0 ? undefined : taxTreatments,
        bonus_payout_types: validPayoutTypes.length === 0 ? undefined : validPayoutTypes
      }
    });
  };

  const handlePayoutTypeChange = (event: SelectChangeEvent<string[]>) => {
    const payoutTypes =
      typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    const currentFilter = (
      exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest
    )?.filter;
    exportFlowStore.updateData({
      filter: {
        ...currentFilter,
        bonus_payout_types: payoutTypes.length === 0 ? undefined : payoutTypes
      }
    });
  };

  const TAX_TREATMENT_OPTIONS = [
    { value: 'SPECIAL_OCCASION', text: t('bonus:SPECIAL_OCCASION:shortName') },
    { value: 'NON_CASH_GIFT', text: t('bonus:NON_CASH_GIFT:shortName') },
    { value: 'NON_CASH_BENEFIT', text: t('bonus:NON_CASH_BENEFIT:shortName') },
    { value: 'FULLY_TAXED', text: t('bonus:FULLY_TAXED:shortName') },
    ...(companyStore.settings?.relief_premium_enabled === true
      ? [{ value: 'RELIEF_PREMIUM_2026', text: t('bonus:RELIEF_PREMIUM_2026:shortName') }]
      : [])
  ];

  const ALL_PAYOUT_TYPE_OPTIONS = [
    { value: 'APP_VOUCHER', text: t('common:VOUCHER') },
    { value: 'EXTERNAL', text: t('common:EXTERNAL') },
    { value: 'SALARY', text: t('bonus:payoutType_SALARY') }
  ];

  const availablePayoutTypes = useMemo(() => {
    if (selectedTaxTreatments.length === 0) return [];
    const payoutTypesFromBonuses = (bonuses?.results ?? [])
      .filter((b) => selectedTaxTreatments.includes(b.tax_treatment ?? ''))
      .map((b) => b.payout_type)
      .filter(Boolean);
    return [...new Set(payoutTypesFromBonuses)];
  }, [bonuses?.results, selectedTaxTreatments]);

  const PAYOUT_TYPE_OPTIONS = ALL_PAYOUT_TYPE_OPTIONS.filter((opt) =>
    availablePayoutTypes.includes(opt.value as PayoutTypeEnum)
  );

  const handleBenefitToggle = (value: string) => {
    const currentFilter = (
      exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest
    )?.filter;
    const current = [...selectedBenefits];
    const updated = current.includes(value as ReceiptBenefitEnum)
      ? current.filter((v) => v !== value)
      : [...current, value as ReceiptBenefitEnum];

    exportFlowStore.updateData({
      filter: { ...currentFilter, benefits: updated.length === 0 ? undefined : updated }
    });
  };

  const handleSelectAll = () => {
    const currentFilter = (
      exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest
    )?.filter;
    exportFlowStore.updateData({
      filter: {
        ...currentFilter,
        benefits: benefitOptions.map((o) => o.value) as ReceiptBenefitEnum[]
      }
    });
  };

  const handleUnselectAll = () => {
    const currentFilter = (
      exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest
    )?.filter;
    exportFlowStore.updateData({
      filter: { ...currentFilter, benefits: undefined }
    });
  };

  const allBenefitsSelected =
    benefitOptions.length > 0 && selectedBenefits.length === benefitOptions.length;

  const getCardStyles = (isSelected?: boolean) => {
    return cn(
      'text-heaven group relative min-h-8 px-3.5 py-2 flex gap-x-4 items-center bg-white border border-solid border-grey-100 rounded-lg cursor-pointer',
      'hover:border-heaven-300 hover:bg-heaven-100',
      isSelected && 'border-heaven-600 bg-heaven-100'
    );
  };

  const currentDate = new Date();
  const targetMonth = format(currentDate, 'MMMM yyyy', { locale: Util.getLocale() });
  const previousMonth = format(subMonths(currentDate, 1), 'MMMM yyyy', {
    locale: Util.getLocale()
  });

  const exportHintsDescription = (
    <span className="text-[12px]">
      <strong className="block text-info-main">{t('payroll:exportHintsTitle')}</strong>
      {hasLateBenefitPositions(currentDate, paymentData?.booked_benefits) && (
        <span className="mb-2 block text-info-main">{t('payroll:completenessHintBody')}</span>
      )}
      <span className="text-info-main">{t('payroll:receiptBenefitsHintBody')}</span>{' '}
      <span className="italic text-grey-500">
        {t('payroll:receiptBenefitsHintExample', { targetMonth, previousMonth })}
      </span>
    </span>
  );

  return (
    <Grid container spacing={2} alignItems="flex-start">
      {type === 'payroll_data' && (
        <Grid item xs={12} marginBottom="4px">
          <FeedbackAlert severity="info" description={exportHintsDescription} />
        </Grid>
      )}
      {type !== 'employee_data' && (
        <Grid item xs={12} md={6}>
          <DatePicker
            label={t('common:PERIOD')}
            variant="month"
            initialValue={format(
              Util.getDate(
                (exportFlowStore.data as CreatePayrollExportRequest | CreateReceiptExportRequest)
                  ?.accounting_period_from
              ),
              'yyyy-MM-dd'
            )}
            onChange={(dateStr) => {
              setError(false);
              exportFlowStore.updateData({
                accounting_period_from: Util.getAccountingPeriod(new Date(dateStr)),
                accounting_period_to: Util.getAccountingPeriod(new Date(dateStr))
              });
            }}
            onChangeInvalidDate={() => setError(true)}
            helperText={t('form:INVALID_DATE')}
            error={error}
            minDate={Util.getMinDate(20)}
            openTo={'month'}
            disableFuture
          />
        </Grid>
      )}
      <Grid item xs={12} md={type !== 'employee_data' ? 6 : 12}>
        <Select
          options={filteredOptions.map((o: ExportFormatEnum) => ({
            value: o,
            text: t(`export:FILE_TYPE_${o}`)
          }))}
          label={t('export:SUMMARY_FIELD_TITLE_FORMAT')}
          placeholder={t('export:SUMMARY_FIELD_TITLE_FORMAT')}
          value={selectedFormat}
          onChange={handleFormatChange}
        />
      </Grid>
      {(type === 'receipts' || type === 'payroll_data') && (
        <Grid item xs={12} md={12}>
          <Box className="flex justify-between items-center mt-4 mb-1">
            <div className="text-xs text-grey-600 mb-1">{t('common:BENEFITS')}</div>
            <Box className="flex gap-1 mb-1">
              <button
                onClick={handleSelectAll}
                className={cn(
                  'text-xs font-medium px-1 py-0.5 rounded cursor-pointer bg-transparent border-none hover:bg-grey-100',
                  allBenefitsSelected
                    ? 'text-heaven-600'
                    : 'text-primary-main hover:text-primary-dark'
                )}
              >
                {t('common:SELECT_ALL')}
              </button>
              <button
                onClick={handleUnselectAll}
                className="text-xs font-medium px-1 py-0.5 rounded cursor-pointer bg-transparent border-none text-primary-main hover:text-primary-dark hover:bg-grey-100"
              >
                {t('common:UNSELECT_ALL')}
              </button>
            </Box>
          </Box>
          <Grid container spacing={1}>
            {benefitOptions.map((option) => {
              const isSelected = selectedBenefits.includes(option.value as ReceiptBenefitEnum);
              return (
                <Grid item xs={3} key={option.value as string}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={getCardStyles(isSelected)}
                    onClick={() => handleBenefitToggle(option.value as string)}
                    onKeyDown={() => handleBenefitToggle(option.value as string)}
                  >
                    {isSelected ? (
                      <CheckmarkCircleFilledIcon className="text-heaven-600 w-6 h-6 shrink-0" />
                    ) : (
                      <CircleIcon className="text-grey-100 group-hover:text-heaven-600 w-6 h-6 shrink-0" />
                    )}
                    <div className="break-all">{option.text}</div>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      )}
      {type === 'payroll_data' && (
        <>
          <Grid item xs={12} md={4} mt={'16px'}>
            <Select
              label={t('common:CUSTOM_BENEFITS')}
              placeholder={t('common:CUSTOM_BENEFITS')}
              tooltip={t('export:CUSTOM_BENEFITS_TOOLTIP')}
              showTooltipOnDisabled
              options={approvedCustomBenefits.map((cb) => ({
                value: cb.id,
                text: cb.name ?? cb.id
              }))}
              value={selectedCustomBenefits}
              onChange={handleCustomBenefitChange}
              disabled={!approvedCustomBenefits.length}
              isMultiple
            />
          </Grid>

          <Grid item xs={12} md={4} mt={'16px'}>
            <Select
              label={t('common:BONUSES')}
              placeholder={t('common:BONUSES')}
              options={TAX_TREATMENT_OPTIONS}
              value={selectedTaxTreatments}
              onChange={handleTaxTreatmentChange}
              disabled={!hasBonuses}
              isMultiple
            />
          </Grid>

          <Grid item xs={12} md={4} mt={'16px'}>
            <Select
              label={t('export:BONUS_PAYOUT_TYPES')}
              placeholder={t('common:all')}
              options={PAYOUT_TYPE_OPTIONS}
              value={selectedPayoutTypes}
              onChange={handlePayoutTypeChange}
              disabled={selectedTaxTreatments.length === 0}
              isMultiple
            />
          </Grid>
        </>
      )}
    </Grid>
  );
});
